/**
 * FASE 4.5 — Schermata pre-export riepilogo & verifica.
 *
 * Mostrata come ultimo step del wizard D&O e Cyber, prima del submit finale.
 * Riepiloga:
 *  - Blocker: errori validazione locale + placeholder detectati
 *  - Warning assuntivi: fattori di attenzione (non bloccanti)
 *  - Anteprima dei dati che verranno stampati sul PDF
 *
 * Preserva accessibilità (role="region", role="alert", aria-labelledby).
 * Mobile-responsive.
 */
import React from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  FileText,
  XCircle,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

export function WizardPreExportSummary({
  formData,
  validationErrors = [],
  warnings = [],
  placeholders = [],
  onProceed,
  onBack,
  submitting = false,
  canProceed = true,
}) {
  const hasBlockers =
    (validationErrors?.length || 0) > 0 || (placeholders?.length || 0) > 0 || !canProceed;

  // Estrai le sezioni con valori non-vuoti (per anteprima)
  const sezioniPreview = React.useMemo(() => {
    if (!formData || typeof formData !== "object") return [];
    const out = [];
    for (const [sezKey, sezVal] of Object.entries(formData)) {
      if (sezKey.startsWith("_")) continue; // salta flag interni
      if (!sezVal || typeof sezVal !== "object" || Array.isArray(sezVal)) continue;
      const campi = [];
      for (const [k, v] of Object.entries(sezVal)) {
        if (v === null || v === undefined || v === "") continue;
        campi.push({ key: k, value: v });
      }
      if (campi.length > 0) out.push({ key: sezKey, campi });
    }
    return out;
  }, [formData]);

  return (
    <motion.section
      role="region"
      aria-labelledby="pre-export-title"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="space-y-5"
      data-testid="wizard-pre-export-summary"
    >
      <header>
        <h2
          id="pre-export-title"
          className="font-heading text-2xl text-[#0F1F14] font-medium"
        >
          Riepilogo e Verifica
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1.5">
          Controlla le informazioni prima dell&apos;invio. Non potrai modificarle
          dopo la sottomissione.
        </p>
      </header>

      {/* BLOCKERS — errori validazione */}
      {validationErrors.length > 0 && (
        <div
          role="alert"
          aria-live="polite"
          data-testid="pre-export-validation-errors"
          className="rounded-2xl border-2 border-red-300 bg-red-50 p-5"
        >
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-600 shrink-0" />
            <h3 className="font-semibold text-red-800 text-base">
              {validationErrors.length === 1
                ? "1 correzione obbligatoria"
                : `${validationErrors.length} correzioni obbligatorie`}
            </h3>
          </div>
          <ul className="text-sm text-red-700 space-y-1 mt-2 ml-7 list-disc list-outside">
            {validationErrors.map((err, i) => (
              <li key={i}>{typeof err === "string" ? err : err.label || String(err)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* PLACEHOLDER */}
      {placeholders.length > 0 && (
        <div
          role="alert"
          data-testid="pre-export-placeholders"
          className="rounded-2xl border-2 border-red-300 bg-red-50 p-5"
        >
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            <h3 className="font-semibold text-red-800 text-base">
              Valori di test rilevati ({placeholders.length})
            </h3>
          </div>
          <p className="text-xs text-red-600/80 mb-2 ml-7">
            In produzione questi valori bloccheranno l&apos;invio. In modalità
            test il PDF verrà marcato con il watermark &quot;TEST&quot;.
          </p>
          <ul className="text-sm text-red-700 space-y-1 mt-2 ml-7 list-disc list-outside">
            {placeholders.slice(0, 20).map((p, i) => (
              <li key={i}>
                <code className="text-xs bg-red-100 px-1 rounded">{p.path}</code>
                : &quot;{p.value}&quot;{" "}
                <span className="text-red-500/80 text-xs">({p.reason})</span>
              </li>
            ))}
            {placeholders.length > 20 && (
              <li className="italic text-red-500">
                …e altri {placeholders.length - 20} valori
              </li>
            )}
          </ul>
        </div>
      )}

      {/* WARNING assuntivi con severity: critical=rosso, warn=ambra, info=blu */}
      {warnings.length > 0 && (() => {
        const norm = warnings.map((w) => (typeof w === "string" ? { text: w, severity: "warn" } : w));
        const critical = norm.filter((w) => w.severity === "critical");
        const rest = norm.filter((w) => w.severity !== "critical");
        return (
          <>
            {critical.length > 0 && (
              <div role="alert" data-testid="pre-export-critical-warnings"
                   className="rounded-2xl border-2 border-red-400 bg-red-50 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-5 h-5 text-red-700 shrink-0" />
                  <h3 className="font-semibold text-red-800 text-base">
                    {critical.length === 1 ? "1 fattore critico" : `${critical.length} fattori critici`} da valutare
                  </h3>
                </div>
                <p className="text-xs text-red-700/80 mb-2 ml-7">
                  Elementi che possono pregiudicare l&apos;assunzione. Non bloccano l&apos;invio ma richiedono attenzione forte.
                </p>
                <ul className="text-sm text-red-800 space-y-1 mt-2 ml-7 list-disc list-outside">
                  {critical.map((w, i) => (<li key={i}><strong>{w.text}</strong></li>))}
                </ul>
              </div>
            )}
            {rest.length > 0 && (
              <div data-testid="pre-export-warnings"
                   className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                  <h3 className="font-semibold text-amber-800 text-base">
                    Fattori di attenzione assuntivi ({rest.length})
                  </h3>
                </div>
                <p className="text-xs text-amber-700 mb-2 ml-7">
                  Le compagnie richiederanno particolare attenzione. <strong>Non bloccano l&apos;invio.</strong>
                </p>
                <ul className="text-sm text-amber-800 space-y-1 mt-2 ml-7 list-disc list-outside">
                  {rest.map((w, i) => (<li key={i}>{w.text}</li>))}
                </ul>
              </div>
            )}
          </>
        );
      })()}

      {/* OK — verde */}
      {!hasBlockers && warnings.length === 0 && (
        <div
          data-testid="pre-export-ok"
          className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5"
        >
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <h3 className="font-semibold text-emerald-800 text-base">
              Questionario pronto per l&apos;invio
            </h3>
          </div>
          <p className="text-sm text-emerald-700 ml-7">
            Nessun errore, incoerenza o warning rilevati. Puoi procedere con la
            sottomissione.
          </p>
        </div>
      )}

      {!hasBlockers && warnings.length > 0 && (
        <div
          data-testid="pre-export-ready-with-warnings"
          className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4"
        >
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>
              Puoi procedere con l&apos;invio. I warning sopra sono informativi.
            </span>
          </div>
        </div>
      )}

      {/* PREVIEW SEZIONI */}
      {sezioniPreview.length > 0 && (
        <details
          className="rounded-2xl border border-[#D9C49A] bg-[#FDF8E8] p-4 open:pb-5"
          data-testid="pre-export-preview"
        >
          <summary className="font-heading text-sm text-[#0F1F14] font-semibold cursor-pointer flex items-center gap-2 select-none">
            <FileText className="w-4 h-4" />
            Anteprima dati stampati sul PDF ({sezioniPreview.length} sezioni)
          </summary>
          <div className="mt-4 space-y-4">
            {sezioniPreview.map((sez) => (
              <div key={sez.key} className="text-xs">
                <h4 className="font-semibold text-[#0F1F14] uppercase tracking-wide text-[10px] mb-1">
                  {sez.key.replace(/_/g, " ")}
                </h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 ml-1">
                  {sez.campi.map((c) => (
                    <div key={c.key} className="flex gap-1.5">
                      <dt className="text-[#4A5E51] shrink-0">
                        {c.key.replace(/_/g, " ")}:
                      </dt>
                      <dd className="text-[#0F1F14] font-medium truncate">
                        {typeof c.value === "boolean"
                          ? c.value ? "Sì" : "No"
                          : String(c.value).slice(0, 60)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* AZIONI */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          data-testid="pre-export-back-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[#D9C49A] bg-white hover:bg-[#FDF8E8] text-[#0F1F14] font-medium text-sm transition-colors disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna a modificare
        </button>
        <button
          type="button"
          onClick={onProceed}
          disabled={hasBlockers || submitting}
          data-testid="pre-export-submit-btn"
          className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex-1 ${
            hasBlockers || submitting
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-[#163321] text-[#D9C49A] hover:bg-[#0F1F14]"
          }`}
        >
          {submitting ? "Invio in corso…" : "Conferma e invia"}
          {!submitting && <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </motion.section>
  );
}

export default WizardPreExportSummary;
